'use client'

import { Plus, FileTextIcon, Video, Archive, Trash2, Clock, CheckSquare, Square } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination'
import { Content } from '../useContentData'
import { getPlatformIcon, getPlatformLabel } from '@/lib/platform-utils'
import { TimeWithTimezone } from '@/components/timezone'

const contentTypeLabels: Record<string, string> = {
  'ad_group': 'Ad Group',
  'blog_post': 'Blog Post',
  'landing_page': 'Landing Page',
  'email_sales_sequence': 'Sales Email Sequence',
  'email_educational_sequence': 'Educational Email Sequence',
  'email_newsletter': 'Email Newsletter',
  'quiz': 'Quiz',
  'video': 'Video',
  'tool': 'Tool',
  'social_post': 'Social Post'
};

const contentTypeColors: Record<string, string> = {
  'ad_group': 'bg-blue-100 text-blue-800',
  'blog_post': 'bg-green-100 text-green-800',
  'email_newsletter': 'bg-purple-100 text-purple-800',
  'email_sales_sequence': 'bg-purple-100 text-purple-800',
  'email_educational_sequence': 'bg-purple-100 text-purple-800',
  'landing_page': 'bg-orange-100 text-orange-800',
  'quiz': 'bg-pink-100 text-pink-800',
  'social_post': 'bg-indigo-100 text-indigo-800',
  'tool': 'bg-yellow-100 text-yellow-800',
  'video': 'bg-red-100 text-red-800'
};

const getContentTypeLabel = (type: string): string => {
  return contentTypeLabels[type] || type;
};

export interface ContentCardViewProps {
  // Content data
  paginatedContents: Content[];
  filteredContents: Content[];
  
  // Pagination
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  
  // Selection state
  selectedContent: Set<string>;
  isSelectionMode: boolean;
  onToggleContentSelection: (contentId: string) => void;
  
  // Actions
  onViewEdit: (contentId: string) => void;
  onChangeSchedule: (content: Content) => void;
  onCreateContent: () => void;
  onPlatformIconClick: (e: React.MouseEvent, platformKey: string | null) => void;
  
  // Utility functions
  getContentImage: (content: Content) => string | null;
  hasVideo: (content: Content) => boolean;
  getTextPreview: (content: Content) => string | null;
}

export function ContentCardView({
  paginatedContents,
  filteredContents,
  currentPage,
  totalPages,
  onPageChange,
  selectedContent,
  isSelectionMode,
  onToggleContentSelection,
  onViewEdit,
  onChangeSchedule,
  onCreateContent,
  onPlatformIconClick,
  getContentImage,
  hasVideo,
  getTextPreview
}: ContentCardViewProps) {
  const navigate = useNavigate();

  // Show empty state if no content
  if (filteredContents.length === 0) {
    return (
      <div className="relative overflow-hidden bg-gradient-to-br from-white via-gray-50/30 to-blue-50/20 rounded-2xl border border-gray-200/50 shadow-sm">
        <div className="absolute inset-0 bg-grid-pattern opacity-5" />
        <div className="relative flex flex-col items-center justify-center py-20 px-8">
          <div 
            className="group relative overflow-hidden w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full flex items-center justify-center mb-6 cursor-pointer hover:from-blue-200 hover:to-indigo-300 transition-all duration-300 transform hover:scale-110 hover:shadow-lg"
            onClick={onCreateContent}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-full" />
            <Plus className="relative h-12 w-12 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
          </div>
          <h3 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent mb-2">No content found</h3>
          <p className="text-gray-600 text-center mb-6 max-w-md leading-relaxed">
            Get started by creating your first content piece and watch your brand come to life
          </p>
          <Button
            onClick={onCreateContent}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105 border-0"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Content
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Enhanced Card Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {paginatedContents.map(content => {
          const contentImage = getContentImage(content);
          const hasVideoContent = hasVideo(content);
          const textPreview = getTextPreview(content);
          
          return (
            <div 
              key={content.id}
              className={`group relative overflow-hidden bg-gradient-to-br from-white via-blue-50/20 to-indigo-50/30 rounded-2xl border ${selectedContent.has(content.id) ? 'border-blue-400 ring-2 ring-blue-200/50' : 'border-blue-200/50'} shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer ${!isSelectionMode ? 'transform hover:-translate-y-2 hover:scale-105' : 'hover:border-blue-300'}`}
              onClick={() => {
                if (isSelectionMode) {
                  onToggleContentSelection(content.id)
                } else {
                  onViewEdit(content.id)
                }
              }}
            >
              {/* Selection indicator overlay */}
              {isSelectionMode && (
                <div className="absolute inset-0 z-30 pointer-events-none">
                  <div className={`absolute inset-0 ${selectedContent.has(content.id) ? 'bg-blue-600/5' : ''} transition-colors duration-200 rounded-2xl`} />
                  <div className="absolute top-3 right-3">
                    <div className={`p-1.5 ${selectedContent.has(content.id) ? 'bg-blue-600' : 'bg-white/80'} backdrop-blur-sm rounded-lg shadow-md transition-all duration-200 transform ${selectedContent.has(content.id) ? 'scale-110' : 'scale-100'}`}>
                      {selectedContent.has(content.id) ? (
                        <CheckSquare className="h-4 w-4 text-white" />
                      ) : (
                        <Square className="h-4 w-4 text-gray-500" />
                      )}
                    </div>
                  </div>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 via-transparent to-purple-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 via-transparent to-indigo-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              
              {/* Content Image/Media Preview */}
              {contentImage || hasVideoContent ? (
                <div className="relative h-48 overflow-hidden rounded-t-2xl bg-gradient-to-br from-gray-100 to-gray-200">
                  {contentImage ? (
                    <>
                      <img 
                        src={contentImage} 
                        alt={content.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = '<div class="flex items-center justify-center h-full bg-gradient-to-br from-gray-100 to-gray-200"><div class="p-4 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm"><div class="h-8 w-8 text-gray-400">📷</div></div></div>';
                          }
                        }}
                      />
                      {hasVideoContent && (
                        <div className="absolute top-3 right-3 p-2 bg-black/50 backdrop-blur-sm rounded-full">
                          <Video className="h-4 w-4 text-white" />
                        </div>
                      )}
                      
                      {/* Platform icon overlay - always visible when there's media */}
                      {content.platform_key && (
                        <div 
                          className="absolute bottom-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-md hover:bg-white hover:shadow-lg transition-all duration-200 cursor-pointer group"
                          onClick={(e) => onPlatformIconClick(e, content.platform_key || null)}
                          title={`View ${getPlatformLabel(content.platform_key)} connection settings`}
                        >
                          {getPlatformIcon(content.platform_key, "h-4 w-4 text-gray-600 group-hover:text-blue-600 transition-colors")}
                        </div>
                      )}
                    </>
                  ) : hasVideoContent ? (
                    <div className="flex items-center justify-center h-full bg-gradient-to-br from-purple-100 to-blue-200">
                      <div className="p-4 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm">
                        <Video className="h-8 w-8 text-purple-600" />
                      </div>
                      
                      {/* Platform icon overlay for video content */}
                      {content.platform_key && (
                        <div 
                          className="absolute bottom-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-lg shadow-md hover:bg-white hover:shadow-lg transition-all duration-200 cursor-pointer group"
                          onClick={(e) => onPlatformIconClick(e, content.platform_key || null)}
                          title={`View ${getPlatformLabel(content.platform_key)} connection settings`}
                        >
                          {getPlatformIcon(content.platform_key, "h-4 w-4 text-gray-600 group-hover:text-blue-600 transition-colors")}
                        </div>
                      )}
                    </div>
                  ) : null}
                  
                  {/* Status indicator overlay - using computed display_status */}
                  <div className="absolute top-3 left-3 flex gap-2">
                    {(() => {
                      const displayStatus = content.display_status || content.status;
                      switch (displayStatus) {
                        case 'ARCHIVED':
                          return (
                            <div className="px-2 py-1 bg-gray-600/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium flex items-center gap-1">
                                <Archive className="h-3 w-3" /> Archived
                              </span>
                            </div>
                          );
                        case 'DELETED':
                          return (
                            <div className="px-2 py-1 bg-red-600/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium flex items-center gap-1">
                                <Trash2 className="h-3 w-3" /> Deleted
                              </span>
                            </div>
                          );
                        case 'PUBLISHED':
                          return (
                            <div className="px-2 py-1 bg-green-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium flex items-center gap-1">
                                ✓ Published
                              </span>
                            </div>
                          );
                        case 'SCHEDULED':
                          return (
                            <div className="px-2 py-1 bg-orange-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium flex items-center gap-1">
                                ⏰ Scheduled
                              </span>
                            </div>
                          );
                        case 'HOLD':
                        case 'HELD':
                          return (
                            <div className="px-2 py-1 bg-yellow-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium flex items-center gap-1">
                                ⏸️ Held
                              </span>
                            </div>
                          );
                        case 'PROCESSING':
                          return (
                            <div className="px-2 py-1 bg-blue-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium flex items-center gap-1">
                                ⚙️ Processing
                              </span>
                            </div>
                          );
                        case 'FAILED':
                          return (
                            <div className="px-2 py-1 bg-red-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium flex items-center gap-1">
                                ❌ Failed
                              </span>
                            </div>
                          );
                        case 'DRAFT':
                          return (
                            <div className="px-2 py-1 bg-blue-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium">Draft</span>
                            </div>
                          );
                        case 'READY':
                          return (
                            <div className="px-2 py-1 bg-gray-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium">Ready</span>
                            </div>
                          );
                        default:
                          return null;
                      }
                    })()}
                  </div>
                </div>
              ) : (
                /* Fallback header with icon */
                <div className="relative h-32 bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center rounded-t-2xl">
                  <div className="p-4 bg-white/80 backdrop-blur-sm rounded-xl shadow-sm">
                    <FileTextIcon className="h-8 w-8 text-blue-600" />
                  </div>
                  
                  {/* Status indicator - using computed display_status */}
                  <div className="absolute top-3 left-3">
                    {(() => {
                      const displayStatus = content.display_status || content.status;
                      switch (displayStatus) {
                        case 'ARCHIVED':
                          return (
                            <div className="px-2 py-1 bg-gray-600/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium flex items-center gap-1">
                                <Archive className="h-3 w-3" /> Archived
                              </span>
                            </div>
                          );
                        case 'DELETED':
                          return (
                            <div className="px-2 py-1 bg-red-600/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium flex items-center gap-1">
                                <Trash2 className="h-3 w-3" /> Deleted
                              </span>
                            </div>
                          );
                        case 'PUBLISHED':
                          return (
                            <div className="px-2 py-1 bg-green-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium">✓ Published</span>
                            </div>
                          );
                        case 'SCHEDULED':
                          return (
                            <div className="px-2 py-1 bg-orange-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium">⏰ Scheduled</span>
                            </div>
                          );
                        case 'HOLD':
                        case 'HELD':
                          return (
                            <div className="px-2 py-1 bg-yellow-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium">⏸️ Held</span>
                            </div>
                          );
                        case 'PROCESSING':
                          return (
                            <div className="px-2 py-1 bg-blue-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium">⚙️ Processing</span>
                            </div>
                          );
                        case 'FAILED':
                          return (
                            <div className="px-2 py-1 bg-red-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium">❌ Failed</span>
                            </div>
                          );
                        case 'DRAFT':
                          return (
                            <div className="px-2 py-1 bg-blue-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium">Draft</span>
                            </div>
                          );
                        case 'READY':
                          return (
                            <div className="px-2 py-1 bg-gray-500/90 backdrop-blur-sm rounded-full">
                              <span className="text-white text-xs font-medium">Ready</span>
                            </div>
                          );
                        default:
                          return null;
                      }
                    })()}
                  </div>
                  
                  {/* Platform icon - clickable */}
                  <div className="absolute top-3 right-3">
                    {content.platform_key && (
                      <div 
                        className="p-2 bg-white/80 backdrop-blur-sm rounded-lg shadow-sm hover:bg-white hover:shadow-md transition-all duration-200 cursor-pointer group"
                        onClick={(e) => onPlatformIconClick(e, content.platform_key || null)}
                        title={`View ${getPlatformLabel(content.platform_key)} connection settings`}
                      >
                        {getPlatformIcon(content.platform_key, "h-4 w-4 text-gray-600 group-hover:text-blue-600 transition-colors")}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              <div className="relative p-6">
                {/* Content Header */}
                <div className="mb-4">
                  <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-700 transition-colors duration-300 line-clamp-2 mb-3">
                    {content.name}
                  </h3>

                  {/* Text Preview */}
                  {textPreview && (
                    <p className="text-sm text-gray-600 line-clamp-3 mb-3 leading-relaxed">
                      {textPreview}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Badge className={`${contentTypeColors[content.content_type]} text-xs font-medium shadow-sm`}>
                      {getContentTypeLabel(content.content_type)}
                    </Badge>
                    {/* Always show platform icon in content details with click functionality */}
                    {content.platform_key && (
                      <div 
                        className="inline-flex items-center gap-1 px-2 py-1 bg-white/60 backdrop-blur-sm rounded-md shadow-sm hover:bg-white hover:shadow-md transition-all duration-200 cursor-pointer group"
                        onClick={(e) => onPlatformIconClick(e, content.platform_key || null)}
                        title={`View ${getPlatformLabel(content.platform_key)} connection settings`}
                      >
                        {getPlatformIcon(content.platform_key, "h-3.5 w-3.5 text-gray-600 group-hover:text-blue-600 transition-colors")}
                        <span className="text-xs text-gray-600 group-hover:text-blue-600 transition-colors font-medium">{getPlatformLabel(content.platform_key)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Content Details */}
                <div className="space-y-3 mb-6">
                  {/* Campaign */}
                  {/* Campaign Display - HIDDEN: Campaigns removed from user flow */}
                  {false && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 font-medium">Campaign</span>
                    <span
                      className="text-blue-600 hover:text-blue-700 font-medium cursor-pointer hover:underline transition-colors max-w-[120px] truncate"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/campaigns/${(content as any).campaigns.id}`);
                      }}
                      title={(content as any).campaigns?.name || 'N/A'}
                    >
                      {(content as any).campaigns?.name || 'N/A'}
                    </span>
                  </div>
                  )}

                  {/* Published Date - Always show */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 font-medium">
                      Published
                    </span>
                    {content.published_at ? (
                      <TimeWithTimezone 
                        date={content.published_at}
                        showRelative={true}
                        showTimezone={false}
                        size="sm"
                        showTooltip={true}
                        relativeMode="smart"
                        preferRelative={true}
                        relativeOptions={{
                          shortFormat: true,
                          absoluteThreshold: 7
                        }}
                        className="text-right max-w-[120px] truncate text-green-600 font-medium"
                      />
                    ) : (
                      <span className="text-gray-500 text-right">Not Published</span>
                    )}
                  </div>
                  
                  {/* Schedule Date - Only show if scheduled */}
                  {content.scheduled_time && !content.published_at && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 font-medium">
                        Scheduled
                      </span>
                      <TimeWithTimezone 
                        date={content.scheduled_time}
                        showRelative={true}
                        showTimezone={false}
                        size="sm"
                        showTooltip={true}
                        relativeMode="smart"
                        preferRelative={true}
                        relativeOptions={{
                          shortFormat: true,
                          absoluteThreshold: 7
                        }}
                        className="text-right max-w-[120px] truncate text-orange-600 font-medium"
                      />
                    </div>
                  )}
                  
                  {/* Created Date */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 font-medium">Created</span>
                    <TimeWithTimezone 
                      date={content.created_at}
                      showTimezone={false}
                      size="sm"
                      showTooltip={true}
                      showRelative={true}
                      relativeMode="contextual"
                      relativeContext="normal"
                      preferRelative={true}
                      className="text-right max-w-[120px] truncate"
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                {!isSelectionMode && (
                  <div className="flex gap-2 mt-auto">
                    {(content.display_status === 'SCHEDULED' || content.is_scheduled === true) && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 bg-white/50 border-white/50 hover:bg-white/70 transition-colors text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          onChangeSchedule(content);
                        }}
                      >
                        <Clock className="h-3 w-3 mr-1" />
                        Reschedule
                      </Button>
                    )}
                    <Button
                      size="sm"
                      className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white shadow-sm hover:shadow-md transition-all duration-300 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewEdit(content.id);
                      }}
                    >
                      View/Edit
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8">
          <Pagination>
            <PaginationContent className="gap-2">
              <PaginationItem>
                <PaginationPrevious 
                  onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
                  className={`${currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:bg-white/70'} bg-white/50 border-white/50 transition-colors`}
                />
              </PaginationItem>
              
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else {
                  const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                  pageNumber = start + i;
                }
                
                return (
                  <PaginationItem key={pageNumber}>
                    <PaginationLink
                      isActive={currentPage === pageNumber}
                      onClick={() => onPageChange(pageNumber)}
                      className={`cursor-pointer transition-colors ${
                        currentPage === pageNumber 
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-transparent hover:from-blue-600 hover:to-indigo-700' 
                          : 'bg-white/50 border-white/50 hover:bg-white/70'
                      }`}
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              
              {totalPages > 5 && currentPage < totalPages - 2 && (
                <PaginationItem>
                  <PaginationEllipsis className="bg-white/30" />
                </PaginationItem>
              )}
              
              <PaginationItem>
                <PaginationNext 
                  onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
                  className={`${currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer hover:bg-white/70'} bg-white/50 border-white/50 transition-colors`}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}